// Rebind an external call-center account to a different ENABLED source
// (#1261). Disabled/archived sources are filtered out of the selector so
// the admin cannot pick an unsafe binding.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CallCenterAccount } from '@magimp/shared';
import { useUpdateCallCenterBinding } from '@magimp/shared-hooks';
import { Modal, Button, Select, FormRow, type SelectOption } from '../../ui/index.js';
import { useLeadSources } from '../../catalog/index.js';
import { useToastStore } from '../../stores/toast.js';
import { errorMessage } from '../../api/errors.js';

export interface CallCenterBindingModalProps {
  account: CallCenterAccount;
  onClose: () => void;
}

export function CallCenterBindingModal({ account, onClose }: CallCenterBindingModalProps) {
  const { t } = useTranslation(['callCenterAdmin', 'common']);
  const [sourceId, setSourceId] = useState<string>(account.binding?.sourceId ?? '');
  const update = useUpdateCallCenterBinding(account.userId);
  const sourcesQ = useLeadSources();
  const sourceOptions: SelectOption[] = (sourcesQ.data ?? [])
    .filter((s) => s.enabled && s.archivedAt === null)
    .map((s) => ({ value: s.id, label: s.displayName }));
  const show = useToastStore((s) => s.show);

  const onSave = () => {
    if (!sourceId) return;
    update.mutate(
      { sourceId },
      {
        onSuccess: () => {
          show({ tone: 'success', text: t('callCenterAdmin:toasts.rebound') });
          onClose();
        },
        onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
      },
    );
  };

  return (
    <Modal open={true} title={t('callCenterAdmin:rebind.title')} width={420} onClose={onClose}>
      <FormRow label={t('callCenterAdmin:fields.source')}>
        <Select
          value={sourceId}
          onChange={setSourceId}
          options={sourceOptions}
          placeholder={t('callCenterAdmin:placeholders.sourceSelect')}
        />
      </FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <Button kind="ghost" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          kind="primary"
          onClick={onSave}
          disabled={update.isPending || !sourceId || sourceId === account.binding?.sourceId}
        >
          {t('common:actions.save')}
        </Button>
      </div>
    </Modal>
  );
}
